import React, { useEffect, useRef } from 'react';
import './CherryBlossoms.css';

/**
 * Falling pink five-petal blossoms (matching the flower used by House Mags), drawn on a single canvas so it stays
 * smooth on phones. Petals drift with a gentle sway, rotate in 3D-ish
 * (squashing as they flip) and fade at the bottom. Pauses when the tab
 * is hidden and respects prefers-reduced-motion.
 */
const COLOURS = ['#fbb4ce', '#fcbcd3', '#f9aac8', '#fdc0d5', '#faafcb'];
const DEEP = '#ee6ba3';
const TIP = '#fde0ea';
const EDGE = '#f08cb5';

function makePetal(w, h, spawnAnywhere) {
  const size = 11 + Math.random() * 9; // blossom radius
  return {
    x: Math.random() * w,
    y: spawnAnywhere ? Math.random() * h : -size * 2 - Math.random() * h * 0.3,
    size,
    vy: 0.5 + Math.random() * 0.9,
    swayAmp: 18 + Math.random() * 34,
    swaySpeed: 0.4 + Math.random() * 0.8,
    phase: Math.random() * Math.PI * 2,
    rot: Math.random() * Math.PI * 2,
    rotSpeed: (Math.random() - 0.5) * 0.03,
    flip: Math.random() * Math.PI * 2,
    flipSpeed: 0.02 + Math.random() * 0.04,
    drift: 0.15 + Math.random() * 0.35,
    colour: COLOURS[Math.floor(Math.random() * COLOURS.length)],
    deep: DEEP,
    tip: TIP,
    edge: EDGE,
    alpha: 0.65 + Math.random() * 0.3,
    baseX: 0,
  };
}

function drawPetal(ctx, p) {
  // A whole five-petal blossom, like the pink flower emoji used on the
  // House Mags page: rounded petals with a small notch at each tip, deep
  // pink at the heart fading to a lighter pink at the edges.
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rot);
  ctx.scale(1, Math.max(0.3, Math.abs(Math.cos(p.flip))));
  ctx.globalAlpha = p.alpha;
  const r = p.size;
  const g = ctx.createRadialGradient(0, 0, r * 0.05, 0, 0, r);
  g.addColorStop(0, p.deep);
  g.addColorStop(0.45, p.colour);
  g.addColorStop(1, p.tip);
  ctx.fillStyle = g;
  ctx.strokeStyle = p.edge;
  ctx.lineWidth = Math.max(0.6, r * 0.05);
  ctx.lineJoin = 'round';
  for (let i = 0; i < 5; i += 1) {
    ctx.save();
    ctx.rotate((i * Math.PI * 2) / 5);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    // left side out to the notched tip, then back down the right side
    ctx.bezierCurveTo(-r * 0.62, -r * 0.12, -r * 0.66, -r * 0.86, -r * 0.2, -r * 0.94);
    ctx.quadraticCurveTo(0, -r * 0.76, r * 0.2, -r * 0.94);
    ctx.bezierCurveTo(r * 0.66, -r * 0.86, r * 0.62, -r * 0.12, 0, 0);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }
  // heart of the flower
  ctx.globalAlpha = p.alpha;
  ctx.fillStyle = p.deep;
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.13, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

export default function CherryBlossoms({ density = 1 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;

    let w = 0;
    let h = 0;
    let petals = [];
    let raf = 0;
    let t = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.round(Math.min(60, Math.max(18, w / 24)) * density);
      petals = Array.from({ length: count }, () => {
        const p = makePetal(w, h, true);
        p.baseX = p.x;
        return p;
      });
    };

    const tick = () => {
      t += 1 / 60;
      ctx.clearRect(0, 0, w, h);
      petals.forEach((p, i) => {
        p.baseX += p.drift;
        p.y += p.vy;
        p.x = p.baseX + Math.sin(t * p.swaySpeed + p.phase) * p.swayAmp;
        p.rot += p.rotSpeed;
        p.flip += p.flipSpeed;
        if (p.y > h + p.size * 2 || p.x > w + 60) {
          const np = makePetal(w, h, false);
          np.baseX = Math.random() * (w + 60) - 60;
          petals[i] = np;
          return;
        }
        // fade out near the bottom
        const fade = p.y > h * 0.85 ? Math.max(0, (h - p.y) / (h * 0.15)) : 1;
        const a = p.alpha;
        p.alpha = a * fade;
        drawPetal(ctx, p);
        p.alpha = a;
      });
      raf = requestAnimationFrame(tick);
    };

    const onVisibility = () => {
      cancelAnimationFrame(raf);
      if (!document.hidden) raf = requestAnimationFrame(tick);
    };

    resize();
    raf = requestAnimationFrame(tick);
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [density]);

  return <canvas ref={canvasRef} className="blossoms" aria-hidden="true" />;
}
